import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useIsFetching } from '@zg-rentals/react-query5';

import Disclaimer from '../../components/Widgets/Disclaimer';
import { isSigningCompleteModalOpenState } from '../../../atoms';
import CountersigningCompleteModal from '../LandlordLeaseView/ListView/LeaseSignPreview/CountersigningCompleteModal';
import { Avatar, Box, Card, Divider, Heading, MediaObject, Paragraph, Toast, useToast } from '@zillow/constellation-10';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';
import { ReviewRenderer } from '@zillow/cosmos';

import { LeaseAction, Property, Summary } from '../../models';
import {
  getCosmosJourneyTokenFromOuid,
  useAttachedLeaseAcceptanceForUserFromOuidQuery,
  useDisclaimerAcceptanceForUserFromOuidQuery,
  useInfoForUser,
  useLandlordProvidedDocumentsFromOuidQuery,
  useLeaseFromOuidQuery,
  useLeaseSummaryFromOuidQuery,
  usePropertyInfoFromOuidQuery,
  useSetAttachedLeaseAcceptanceForUserFromOuidMutation,
  useSetDisclaimerAcceptanceForUserFromOuidMutation,
  useWhoHasSignedFromOuidQuery,
} from '../../utils/queries';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import type { LandlordProvidedDocument } from '../../models/Types';
import { DocumentBackend } from '../../models/Types';
import { SUMMARY_VIEW_SUBTYPES } from '../../models/Lease';
import SummaryTitle from '../../components/LeaseSummary/SummaryTitle';
import LandlordProvidedDocuments from '../../components/Widgets/LandlordProvidedDocuments';
import IdentityContentBox from '../../components/Widgets/IdentityContentBox';
import { getZillowLeasesDisclaimer } from '../../components/RequestSignatureModal/constants';
import UserInfoVerifier from '../../components/UserInfoVerifier';
import DocumentPreviewSelector from '../../components/DocumentPreviewSelector';
import LeaseAttachedCheckbox from '../../components/Widgets/LeaseAttachedCheckbox';
import SummaryReadOnly from '../../components/LeaseSummary/SummaryReadOnly';
import { ALL_US_STATES_TERRITORIES_AND_CANADA_MAP } from '@zg-rentals/states-territories';
import { useLeasingUrlParams } from '../../hooks/useLeasingUrlParams';
import NoAccessLeaseAlert from '../../components/Widgets/NoAccessLeaseAlert';

export function ColandlordLeaseSummary() {
  const history = useHistory();
  const { leaseId, nestedViewType } = useLeasingUrlParams();
  const { enqueueToast } = useToast();

  const queryParams = new URLSearchParams(window.location.search);
  const ouid = queryParams.get('ouid');

  const infoForUserQuery = useInfoForUser(ouid, !!ouid);
  const leaseQuery = useLeaseFromOuidQuery(ouid, !!ouid);
  const summaryQueryData = useLeaseSummaryFromOuidQuery(ouid, !!ouid);
  const propertyQuery = usePropertyInfoFromOuidQuery(ouid, !!ouid);
  const landlordProvidedDocumentsQuery = useLandlordProvidedDocumentsFromOuidQuery(ouid, !!ouid);
  const disclaimerAcceptanceQuery = useDisclaimerAcceptanceForUserFromOuidQuery(ouid, !!ouid);
  const attachedLeaseAcceptanceQuery = useAttachedLeaseAcceptanceForUserFromOuidQuery(ouid, !!ouid);

  useWhoHasSignedFromOuidQuery(ouid, !!ouid);

  const setDisclaimerAcceptanceMutation = useSetDisclaimerAcceptanceForUserFromOuidMutation();
  const setAttachedLeaseAcceptanceMutation = useSetAttachedLeaseAcceptanceForUserFromOuidMutation();

  const lease = leaseQuery.data;
  const property = propertyQuery.data ? new Property(propertyQuery.data) : null;
  const summaryData = summaryQueryData.data;
  const summary = summaryData?.summary ? new Summary({ sectionCategories: summaryData.summary }) : null;
  const summaryText = summaryData?.summaryText || '';
  const summaryActions =
    summaryData?.summaryActions?.map((params: any) => new LeaseAction(params.name, params.disabled)) || [];
  const landlordProvidedDocuments: Array<LandlordProvidedDocument> = (
    landlordProvidedDocumentsQuery.data?.documentNames || []
  ).map((doc: any) => {
    if (typeof doc === 'object' && doc !== null && 'text' in doc) {
      return doc;
    }
    return { text: String(doc) };
  });
  const acceptedDisclaimer = disclaimerAcceptanceQuery.isLoading
    ? undefined
    : (disclaimerAcceptanceQuery.data?.acceptedDisclaimer ?? false);
  const acceptedAttachedLease = attachedLeaseAcceptanceQuery.isLoading
    ? false
    : (attachedLeaseAcceptanceQuery.data?.acceptedAttachedLease ?? false);
  const participant = infoForUserQuery.data;

  const [canClickCta, setCanClickCta] = useState(false);
  const [acceptedAttachedLeaseCheckbox, setAcceptedAttachedLeaseCheckbox] = useState(false);
  const [isAcceptingDisclaimer, setIsAcceptingDisclaimer] = useState(false);
  const [isReviewRendererLoading, setIsReviewRendererLoading] = useState(false);
  const [hasRenderedReviewRenderer, setHasRenderedReviewRenderer] = useState(false);

  useEffect(() => {
    setAcceptedAttachedLeaseCheckbox(acceptedAttachedLease);
  }, [acceptedAttachedLease]);

  // Track when we're rendering a ReviewRenderer so we know to wait for it to load
  useEffect(() => {
    if (lease?.journeyId) {
      setHasRenderedReviewRenderer(true);
      setIsReviewRendererLoading(true);
    }
  }, [lease?.journeyId]);

  const [isSigningCompleteModalOpen, setIsSigningCompleteModalOpen] = useRecoilState(isSigningCompleteModalOpenState);

  const isFetching = useIsFetching();

  function onCloseLeasePreview() {
    if (!lease) return;
    history.push(lease.getSummaryPath());
  }

  function onLeasePreviewError(notification: string) {
    onCloseLeasePreview();
    enqueueToast(<Toast tone="critical">{notification}</Toast>);
  }

  function onToggleCanClickCta() {
    setCanClickCta(!canClickCta);
  }

  async function onCtaClicked() {
    if (!ouid || isAcceptingDisclaimer) return;

    setIsAcceptingDisclaimer(true);

    const promises: Array<Promise<unknown>> = [];

    if (acceptedAttachedLeaseCheckbox) {
      promises.push(setAttachedLeaseAcceptanceMutation.mutateAsync({ getParams: { ouid } }));
    }

    if (canClickCta) {
      promises.push(setDisclaimerAcceptanceMutation.mutateAsync({ getParams: { ouid } }));
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }

    setIsAcceptingDisclaimer(false);
  }

  function renderPrimaryAction(disabled?: boolean) {
    return summaryActions ? (
      <LeaseActionButtonGroup
        leaseActions={summaryActions}
        fluid
        lease={lease}
        property={property}
        disabled={disabled || isAcceptingDisclaimer}
        data-test="lease-summary-action-button"
        onPrimaryActionClick={acceptedDisclaimer ? null : onCtaClicked}
      />
    ) : null;
  }

  function renderDisclaimerSubText() {
    return (
      <Paragraph css={{ textStyle: 'body-xs', color: 'text.subtle' }}>
        {lease.isSelfUpload ? (
          <>
            If you have any questions about the legal rights, obligations, or implications of your lease, please consult
            with an attorney
          </>
        ) : (
          <>
            If you have any questions about the legal rights, obligations, enforceability, or implications of this
            lease, please consult with an attorney
          </>
        )}
      </Paragraph>
    );
  }

  function onToggleAttachedLeaseAccepted() {
    setAcceptedAttachedLeaseCheckbox(!acceptedAttachedLeaseCheckbox);
  }

  function renderDisclaimer() {
    const state = property && property.state;
    const landlordDisclaimer = getZillowLeasesDisclaimer(ALL_US_STATES_TERRITORIES_AND_CANADA_MAP[state]);
    const hasDocuments = landlordProvidedDocuments.length > 0;

    return (
      <Disclaimer
        beforeCheckboxContent={
          hasDocuments && (
            <>
              <Heading level={3} css={{ textStyle: 'responsive.body-lg-bold' }}>
                Did you get {landlordProvidedDocuments.length === 1 ? 'this document' : 'these documents'}?
              </Heading>
              <Paragraph>
                {lease.documentBackend === DocumentBackend.dotloop
                  ? `Be sure to review ${landlordProvidedDocuments.length === 1 ? 'it' : 'them'} before signing your lease, or contact your co-landlord if you didn’t receive the ${landlordProvidedDocuments.length === 1 ? 'document' : 'documents'}.`
                  : `Contact your co-landlord if you don’t see the ${landlordProvidedDocuments.length === 1 ? 'document' : 'documents'} in your lease package.`}
              </Paragraph>
              <LandlordProvidedDocuments
                landlordProvidedDocuments={landlordProvidedDocuments}
                hideExcessDocuments={false}
                showIncludedText={false}
                showAsOrderedList
              />
            </>
          )
        }
        label="I agree to these terms before viewing"
        afterCheckboxContent={
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
            {renderDisclaimerSubText()}
            <LeaseAttachedCheckbox checked={acceptedAttachedLeaseCheckbox} onClick={onToggleAttachedLeaseAccepted} />
            {renderPrimaryAction(!canClickCta || acceptedDisclaimer)}
          </Box>
        }
        isChecked={canClickCta}
        onClick={onToggleCanClickCta}
        checkboxLabel={landlordDisclaimer}
      />
    );
  }

  function renderLandlordProvidedDocumentsContainer() {
    if (lease == null || !participant || landlordProvidedDocumentsQuery.isLoading) {
      return (
        <>
          <MediaObject media={<Avatar size="lg" />}>
            <LoadingText isLoading loadingWidth="60%" css={{ marginBottom: 'tight' }}>
              Loading
            </LoadingText>
            <LoadingText isLoading loadingWidth="90%" css={{ marginBottom: 'tighter' }}>
              Loading
            </LoadingText>
          </MediaObject>
          <Divider css={{ marginBottom: 'tight', marginTop: 'tight' }} />
          <Card tone="neutral" elevated={false} css={{ marginBottom: 'tight' }}>
            <LoadingHeading
              isLoading
              level={3}
              loadingWidth="40%"
              css={{ marginBottom: 'tighter', textStyle: 'responsive.body-lg-bold' }}
            >
              Loading
            </LoadingHeading>
            <LoadingText isLoading loadingWidth="90%" css={{ marginBottom: 'tight' }}>
              Loading
            </LoadingText>
            <Box css={{ marginBottom: 'tight' }}>
              <LoadingText isLoading loadingWidth="50%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="45%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="40%" css={{ marginBottom: 'tight' }}>
                Loading
              </LoadingText>
            </Box>
            <LoadingHeading
              isLoading
              level={3}
              loadingWidth="50%"
              css={{ marginBottom: 'tighter', textStyle: 'responsive.body-lg-bold' }}
            >
              Loading
            </LoadingHeading>
            <Box css={{ marginBottom: 'tight' }}>
              <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="95%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="85%" css={{ marginBottom: 'tight' }}>
                Loading
              </LoadingText>
            </Box>
            <LoadingText isLoading loadingWidth="70%" css={{ marginBottom: 'tight' }}>
              Loading
            </LoadingText>
            <LoadingHeading
              isLoading
              level={3}
              loadingWidth="45%"
              css={{ marginBottom: 'tighter', textStyle: 'responsive.body-lg-bold' }}
            >
              Loading
            </LoadingHeading>
            <LoadingText isLoading loadingWidth="75%" css={{ marginBottom: 'tight' }}>
              Loading
            </LoadingText>
            <LoadingButton isLoading>View full lease</LoadingButton>
          </Card>
        </>
      );
    }

    return (
      <IdentityContentBox firstName={participant.firstName} lastName={participant.lastName} email={participant.email}>
        {renderDisclaimer()}
      </IdentityContentBox>
    );
  }

  function renderHeader() {
    const propertyAddress = property?.streetAddress;
    const personFirstName = lease?.landlords?.[1]?.firstName;
    const leaseStartDate = lease?.getFriendlyStartDate();
    const isSelfUploadLease = lease?.isSelfUpload;

    const shouldShowSkeleton =
      !lease ||
      (acceptedDisclaimer === undefined && landlordProvidedDocumentsQuery.isLoading) ||
      (!isSelfUploadLease && acceptedDisclaimer === false && landlordProvidedDocumentsQuery.isLoading);

    if (shouldShowSkeleton) {
      return (
        <Box css={{ marginBottom: 'tight' }}>
          <LoadingHeading
            isLoading
            level={2}
            loadingWidth="60%"
            css={{ textStyle: 'responsive.heading-xs', marginBottom: 'tighter' }}
          >
            Loading
          </LoadingHeading>
          <LoadingText isLoading loadingWidth="40%" css={{ marginBottom: 'tight' }}>
            Loading
          </LoadingText>
          {renderLandlordProvidedDocumentsContainer()}
        </Box>
      );
    }

    return (
      <Box css={{ marginBottom: 'tight' }}>
        <SummaryTitle {...{ summaryText, propertyAddress, personFirstName, leaseStartDate }} />
        {!isSelfUploadLease && !acceptedDisclaimer
          ? renderLandlordProvidedDocumentsContainer()
          : renderPrimaryAction(false)}
      </Box>
    );
  }

  function generateJourneyToken() {
    if (!ouid) return Promise.reject('No OUID');
    return getCosmosJourneyTokenFromOuid(ouid);
  }

  function renderSummary() {
    const showSkeleton =
      !lease ||
      (lease && !lease.journeyId && !summary) ||
      (lease?.journeyId && (!hasRenderedReviewRenderer || isReviewRendererLoading)) ||
      isFetching > 0;

    const skeleton = (
      <>
        {[1, 2, 3, 4, 5].map((index) => (
          <Card key={index} tone="neutral" elevated={false} css={{ marginBottom: 'tighter' }}>
            <LoadingHeading
              isLoading
              level={3}
              loadingWidth="40%"
              css={{ marginBottom: 'loose', textStyle: 'responsive.heading-xs' }}
            >
              Loading
            </LoadingHeading>
            {[1, 2, 3].map((rowIndex) => (
              <Box key={rowIndex} css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
                  <LoadingText isLoading loadingWidth="50%">
                    Loading
                  </LoadingText>
                </Box>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
                  <LoadingText isLoading loadingWidth="70%">
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="40%" css={{ marginBottom: 'tighter' }}>
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="60%">
                    Loading
                  </LoadingText>
                </Box>
              </Box>
            ))}
          </Card>
        ))}
      </>
    );

    // If we only need to show skeleton and no actual content needs to be mounted
    if (!lease || (lease && !lease.journeyId && !summary)) {
      return skeleton;
    }

    // Render both skeleton and actual content, hiding the content until loaded
    return (
      <>
        {showSkeleton && skeleton}
        <Box css={{ visibility: showSkeleton ? 'hidden' : 'visible', height: showSkeleton ? '0' : 'auto' }}>
          {lease.journeyId ? (
            <ReviewRenderer
              journeyId={lease.journeyId}
              journeyVersionId={lease.journeyVersionId}
              generateJourneyToken={generateJourneyToken}
              isReadOnly
              setIsReviewRendererLoading={setIsReviewRendererLoading}
            />
          ) : (
            <SummaryReadOnly lease={lease} summary={summary} />
          )}
          <Box css={{ marginTop: 'tight' }}>
            {!acceptedDisclaimer ? renderLandlordProvidedDocumentsContainer() : renderPrimaryAction(false)}
          </Box>
        </Box>
      </>
    );
  }

  function renderNestedView() {
    switch (nestedViewType) {
      case SUMMARY_VIEW_SUBTYPES.PREVIEW:
        return (
          <DocumentPreviewSelector
            leaseId={leaseId}
            lease={lease}
            property={property}
            isColandlord
            onClose={onCloseLeasePreview}
            onError={onLeasePreviewError}
          />
        );
      default:
        return null;
    }
  }

  function handleCloseSigningModal() {
    setIsSigningCompleteModalOpen(false);
  }

  return (
    <>
      <UserInfoVerifier leaseId={leaseId} isColandlord />
      {lease && property && (
        <CountersigningCompleteModal
          isOpen={isSigningCompleteModalOpen}
          lease={lease}
          property={property}
          isColandlord={true}
          onClose={handleCloseSigningModal}
        />
      )}
      <>
        {!ouid || leaseQuery.isError ? (
          <NoAccessLeaseAlert />
        ) : (
          <>
            {renderHeader()}
            {renderSummary()}
            {renderNestedView()}
          </>
        )}
      </>
    </>
  );
}

export default ColandlordLeaseSummary;
